import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  ShoppingBag,
  Menu,
  X,
  Heart,
  UserRound,
  LogOut,
  LayoutDashboard,
} from "lucide-react";
import { apiFetch, resolveImageUrl } from "../lib/api";

export default function Header({
  user,
  cartCount,
  wishCount,
  onLogout,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [cats, setCats] = useState([]);
  const [settings, setSettings] = useState({});
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;

    Promise.all([
      apiFetch("/categories"),
      apiFetch("/site-settings"),
    ])
      .then(([categories, siteSettings]) => {
        if (!mounted) return;

        setCats(Array.isArray(categories) ? categories : []);
        setSettings(siteSettings || {});
      })
      .catch(() => {
        if (!mounted) return;

        setCats([]);
        setSettings({});
      });

    return () => {
      mounted = false;
    };
  }, []);

  /*
   * Keep the browser favicon and mobile home-screen icon
   * synchronized with the logo configured in:
   *
   * Admin → Site Settings → Brand → Logo
   *
   * This means changing the logo from the Admin workspace
   * automatically changes the site's favicon as well.
   */
  useEffect(() => {
    const logoUrl = settings?.logo_url
      ? resolveImageUrl(settings.logo_url)
      : "";

    const manageIcon = (
      selector,
      rel,
      href,
      sizes = null
    ) => {
      let link = document.head.querySelector(selector);

      if (!href) {
        if (link) {
          link.remove();
        }
        return;
      }

      if (!link) {
        link = document.createElement("link");
        link.rel = rel;

        if (sizes) {
          link.sizes = sizes;
        }

        document.head.appendChild(link);
      }

      link.href = href;
    };

    if (logoUrl) {
      manageIcon(
        'link[data-gmk-icon="favicon"]',
        "icon",
        logoUrl
      );

      manageIcon(
        'link[data-gmk-icon="apple-touch-icon"]',
        "apple-touch-icon",
        logoUrl,
        "180x180"
      );
    } else {
      const favicon = document.head.querySelector(
        'link[data-gmk-icon="favicon"]'
      );

      const appleIcon = document.head.querySelector(
        'link[data-gmk-icon="apple-touch-icon"]'
      );

      if (favicon) {
        favicon.remove();
      }

      if (appleIcon) {
        appleIcon.remove();
      }
    }

    return () => {
      const favicon = document.head.querySelector(
        'link[data-gmk-icon="favicon"]'
      );

      const appleIcon = document.head.querySelector(
        'link[data-gmk-icon="apple-touch-icon"]'
      );

      if (favicon) {
        favicon.remove();
      }

      if (appleIcon) {
        appleIcon.remove();
      }
    };
  }, [settings.logo_url]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const submit = (e) => {
    e.preventDefault();

    if (search.trim()) {
      nav(
        `/shawls?search=${encodeURIComponent(
          search.trim()
        )}`
      );
    }

    setOpen(false);
  };

  const isAdmin =
    user &&
    ["owner", "admin", "staff"].includes(user.role);

  return (
    <>
      <div
        className="announcement"
        data-testid="announcement-bar"
      >
        Complimentary consultation for your next heirloom piece{" "}
        <span>·</span>
        <Link to="/about">Discover our story</Link>
      </div>

      <header className="header">
        <Link
          className="logo"
          to="/"
          data-testid="brand-logo"
        >
          {settings.logo_url ? (
            <img
              src={resolveImageUrl(settings.logo_url)}
              alt={
                settings.brand_name ||
                "GMK Handicrafts Emporium"
              }
              className="logo-img"
            />
          ) : (
            <span
              className="logo-mark"
              aria-hidden
            >
              ✦
            </span>
          )}

          <span>
            GMK <b>Handicrafts</b>
            <small>EMPORIUM</small>
          </span>
        </Link>

        <nav
          className={open ? "nav open" : "nav"}
          data-testid="main-navigation"
        >
          <Link
            to="/"
            data-testid="nav-home"
          >
            Home
          </Link>

          <Link
            to="/shawls"
            data-testid="nav-shawls"
          >
            Shawls
          </Link>

          <Link
            to="/about"
            data-testid="nav-about"
          >
            About
          </Link>

          <Link
            to="/contact"
            data-testid="nav-contact"
          >
            Contact
          </Link>
        </nav>

        <div className="header-actions">
          <form
            className="search"
            onSubmit={submit}
          >
            <Search size={17} />

            <input
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              placeholder="Search collection"
              data-testid="search-input"
              aria-label="Search"
            />
          </form>

          <Link
            to="/account?tab=wishlist"
            className="icon-link"
            data-testid="wishlist-link"
            aria-label="Wishlist"
          >
            <Heart size={19} />

            {wishCount > 0 && (
              <em>{wishCount}</em>
            )}
          </Link>

          {isAdmin && (
            <Link
              to="/admin"
              className="icon-link admin-badge"
              title="Admin workspace"
              data-testid="admin-link"
            >
              <LayoutDashboard size={19} />
            </Link>
          )}

          {user ? (
            <div
              className="account-chip"
              data-testid="account-chip"
            >
              <Link
                to="/account"
                className="icon-link"
                aria-label="Account"
              >
                <UserRound size={19} />
              </Link>

              <button
                onClick={onLogout}
                className="icon-link"
                aria-label="Log out"
                title="Log out"
                data-testid="logout-button"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              className="icon-link"
              aria-label="Sign in"
              data-testid="login-link"
            >
              <UserRound size={19} />
            </Link>
          )}

          <Link
            to="/checkout"
            className="bag"
            data-testid="cart-link"
          >
            <ShoppingBag size={19} />

            {cartCount > 0 && (
              <em>{cartCount}</em>
            )}
          </Link>

          <button
            className="menu-btn"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-button"
            aria-label={
              open
                ? "Close menu"
                : "Open menu"
            }
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>
    </>
  );
}
